import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Legal.css';

const Privacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-container">
      <div className="legal-content-wrapper">
        
        {/* Navigation Bar */}
        <div className="legal-nav-header">
          <div className="legal-nav-left">
            <button className="legal-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>BACK</span>
            </button>
            <div className="legal-breadcrumb-badge">
              <span>CRACKIT AI</span>
              <span className="crumb-separator">/</span>
              <span>LEGAL PROTOCOLS</span>
              <span className="crumb-separator">/</span>
              <span className="crumb-current">PRIVACY POLICY</span>
            </div>
          </div>

          <div className="legal-tabs-switch">
            <Link to="/terms" className="legal-tab-link">Terms of Service</Link>
            <Link to="/privacy" className="legal-tab-link active-legal-tab">Privacy Policy</Link>
            <Link to="/refund-policy" className="legal-tab-link">Refund Policy</Link>
          </div>
        </div>

        {/* Header Banner */}
        <motion.div 
          className="legal-header-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <span className="legal-category-badge">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Data Protection & Privacy
          </span>
          <h1 className="legal-main-title">PRIVACY POLICY</h1>
          <div className="legal-meta-row">
            <span className="legal-meta-pill">
              <strong>Effective:</strong> September 19, 2026
            </span>
            <span className="legal-meta-pill">
              <strong>Compliance:</strong> GDPR, CCPA & Indian IT Act
            </span>
            <span className="legal-meta-pill">
              <strong>Contact:</strong> support@crackit.si
            </span>
          </div>
        </motion.div>

        {/* Main Document Body */}
        <motion.div 
          className="legal-body-card"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
        >
          
          <div className="legal-callout-box callout-info">
            <div className="callout-icon-wrapper">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="callout-body">
              <h4>Your Privacy is Paramount</h4>
              <p>
                CrackIT AI was built with strict privacy-by-design principles. We never sell your personal data, never train public foundational models on your private transcripts, and encrypt all network traffic with 256-bit SSL.
              </p>
            </div>
          </div>

          <section className="legal-section">
            <h2>1. Information We Collect</h2>
            <p>
              We collect information strictly necessary to provide real-time interview speech recognition, AI answer generation, and account management:
            </p>
            <ul>
              <li><strong>Account Information:</strong> When you authenticate via Google OAuth, we receive your name, email address, profile photo URL, and unique Google ID.</li>
              <li><strong>Interview Session Data:</strong> Audio input processed during live sessions for speech-to-text transcription, user-initiated screen OCR snapshots, and uploaded PDF resumes/career notes.</li>
              <li><strong>Transaction Records:</strong> Razorpay Payment IDs, purchased credit amounts, and transaction timestamps. We do NOT store credit card numbers, CVVs, or bank PINs on our servers.</li>
              <li><strong>Technical Metadata:</strong> Browser type, operating system version, IP address, and application performance diagnostic metrics.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>2. How We Use Your Information</h2>
            <p>
              Your data is processed strictly for the following functional purposes:
            </p>
            <ul>
              <li><strong>Delivering Core AI Features:</strong> Processing live audio feeds to transcribe interviewer questions and generating contextual coding/system design suggestions in real time.</li>
              <li><strong>Account & Balance Management:</strong> Deducting credits accurately based on active session minutes and maintaining your interview history log.</li>
              <li><strong>Security & Abuse Prevention:</strong> Detecting anomalous bot activity, payment fraud, and unauthorized concurrent logins.</li>
              <li><strong>Customer Support:</strong> Assisting you when you report technical issues or request credit top-up investigations.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>3. Real-Time Audio & Screen OCR Data Handling</h2>
            <p>
              We treat your live interview audio and screen data with the highest security standards:
            </p>
            <ul>
              <li><strong>Ephemeral Audio Processing:</strong> Audio streams are converted to text in real time via secure encrypted WebSockets and are not kept in long-term raw audio audio archives.</li>
              <li><strong>Screen Privacy:</strong> Screen OCR captures are only taken when you explicitly trigger the "Analyze Screen" hotkey or button on the desktop overlay.</li>
              <li><strong>User Deletion Control:</strong> You have full control over your session history and can permanently delete any session transcript from your Dashboard at any time.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>4. Third-Party Service Processors</h2>
            <p>
              We partner with trusted, enterprise-grade infrastructure providers who comply with industry security standards:
            </p>
            <ul>
              <li><strong>AI LLM Providers:</strong> OpenAI (GPT-4o), Anthropic (Claude 3.5 Sonnet), and Google Cloud (Gemini 1.5 Pro). We use enterprise API endpoints where inputs are not used to train public models.</li>
              <li><strong>Payment Gateway:</strong> Razorpay Software Private Limited (PCI-DSS Level 1 compliant).</li>
              <li><strong>Database & Cloud Hosting:</strong> MongoDB Atlas (AES-256 encrypted at rest) and cloud servers with TLS 1.3 encryption in transit.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>5. Data Security Measures</h2>
            <p>
              CrackIT AI implements multi-layered security controls to protect your personal data:
            </p>
            <ul>
              <li>End-to-end 256-bit SSL/TLS encryption for all HTTP and WebSocket network communications.</li>
              <li>Secure JSON Web Token (JWT) session authorization.</li>
              <li>Role-based access controls restricting backend server access strictly to authorized engineering personnel.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>6. Your Data Rights (GDPR / CCPA / IT Act)</h2>
            <p>
              Depending on your location, you have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Right of Access:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate personal data.</li>
              <li><strong>Right to Erasure:</strong> Request permanent deletion of your account and all associated interview transcripts.</li>
              <li><strong>Right to Restrict Processing:</strong> Request restrictions on how your data is processed.</li>
            </ul>
            <p>
              To exercise any of these rights, simply email us at <strong>support@crackit.si</strong> with your registered email address.
            </p>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>7. Contact Our Privacy Officer</h2>
            <p>
              For any questions, concerns, or data requests regarding this Privacy Policy, please contact our Data Protection Officer:
            </p>
          </section>

          {/* Contact Card */}
          <div className="legal-contact-card">
            <h3>Privacy & Data Inquiries</h3>
            <p>We typically respond within 24 to 48 business hours:</p>
            <a href="mailto:support@crackit.si" className="legal-email-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect width="20" height="16" x="2" y="4" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <span>support@crackit.si</span>
            </a>
          </div>

        </motion.div>

      </div>
    </div>
  );
};

export default Privacy;
